import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { toast } from "sonner";
import { Package, Barcode, ScanLine, Tags, ArrowDown, Mail, Lock } from "lucide-react";
import { DashboardShowcase, ScannerShowcase, PrintTagShowcase } from "../components/Showcase";
import { PricingSection, TestimonialsSection, FAQSection } from "../components/LandingSections";

export default function Landing() {
  const navigate = useNavigate();
  const { user, loading, setUser } = useAuth();

  React.useEffect(() => {
    if (!loading && user) navigate("/dashboard", { replace: true });
  }, [user, loading, navigate]);

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Scroll the visitor up to the AuthCard when a pricing CTA is tapped.
  const goSignup = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen flex flex-col">
      <div className="grid grid-cols-1 lg:grid-cols-2 lg:min-h-screen">
      {/* LEFT */}
      <div className="kraft-bg relative px-6 py-10 sm:px-10 sm:py-14 lg:p-16 flex flex-col justify-between overflow-hidden gap-10 lg:gap-0">
        {/* decorative bg image */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.07] bg-cover bg-center mix-blend-multiply"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&w=1600&q=60')" }}
        />
        <div className="absolute -right-10 -top-10 w-80 h-80 rounded-full opacity-[0.06] bg-[#C2410C] blur-3xl" />

        <div className="relative flex items-center gap-3">
          <div className="w-10 h-10 bg-[#C2410C] border-2 border-black flex items-center justify-center shadow-[3px_3px_0_0_#111]">
            <Package className="text-white" size={22} />
          </div>
          <div className="font-heading font-black tracking-tight text-xl uppercase">Vendor Vault</div>
        </div>

        <div className="relative my-10 snap-in">
          <span className="stamp stamp-float mb-6" data-testid="landing-stamp">Vendor-Owned</span>
          <h1 className="font-heading text-4xl sm:text-5xl md:text-7xl font-black uppercase leading-[0.92] tracking-tight mt-6">
            Inventory,<br/>
            <span className="text-[#C2410C]">Forged.</span>
          </h1>
          <p className="mt-6 max-w-lg font-mono text-sm md:text-base text-[#4A4A4A] leading-relaxed">
            Made for the makers — track every sticker, shirt, and one-of-one.
            Print barcodes, log sales by show, and know exactly what to restock
            before the next gate opens.
          </p>
        </div>

        <div className="relative grid grid-cols-2 gap-4 max-w-md">
          <Feature icon={<Tags size={18} />} label="Variants" />
          <Feature icon={<Barcode size={18} />} label="Barcodes" />
          <Feature icon={<ScanLine size={18} />} label="Scan-to-Lookup" />
          <Feature icon={<Package size={18} />} label="Event Sales" />
        </div>
      </div>

      {/* RIGHT */}
      <div className="bg-white dot-grid px-6 py-10 sm:px-10 sm:py-14 lg:p-16 flex items-center justify-center relative border-t-2 lg:border-t-0 lg:border-l-2 border-black">
        <div className="absolute top-0 left-0 right-0 accent-strip" />
        <AuthCard
          onGoogle={handleGoogleLogin}
          onAuthenticated={(data) => {
            if (data?.session_token) localStorage.setItem("session_token", data.session_token);
            try { localStorage.setItem("vv_user", JSON.stringify(data.user)); } catch (_) { /* ignore */ }
            setUser(data.user);
            navigate("/dashboard", { replace: true });
          }}
        />
      </div>
      </div> {/* end of split hero */}

      {/* Scroll cue */}
      <div className="hidden lg:flex justify-center -mt-2 mb-6">
        <div className="brutal-badge !bg-white flex items-center gap-2 animate-pulse">
          <ArrowDown size={12}/> See it in action
        </div>
      </div>

      {/* SHOWCASE SECTION */}
      <section className="bg-[#F4F2EC] border-t-2 border-black relative overflow-hidden">
        <div className="accent-strip" />
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 py-16 lg:py-24">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10 lg:mb-14">
            <div>
              <span className="brutal-badge mb-3" data-testid="showcase-pill">Field-tested</span>
              <h2 className="font-heading text-4xl md:text-5xl font-black uppercase tracking-tight mt-3">
                Three screens.<br/>One booth-ready loop.
              </h2>
            </div>
            <p className="font-mono text-sm text-[#4A4A4A] max-w-md leading-relaxed">
              From the morning setup to the post-event payout — every step is one tap away.
              No spreadsheets. No printer drivers. No drama.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10">
            <ShowcaseCard
              step="01"
              title="Track at a glance"
              copy="Live dashboard with low-stock alerts, revenue, and top movers per show."
              testid="showcase-dashboard"
            >
              <DashboardShowcase/>
            </ShowcaseCard>

            <ShowcaseCard
              step="02"
              title="Scan with your phone"
              copy="Webcam barcode lookup. Adjust stock or log a sale in two taps. Works offline-first."
              testid="showcase-scanner"
              accent
            >
              <ScannerShowcase/>
            </ShowcaseCard>

            <ShowcaseCard
              step="03"
              title="Print booth-ready tags"
              copy="Auto-generated SKUs and Code128 barcodes on a printable shipping-tag layout."
              testid="showcase-print"
            >
              <PrintTagShowcase/>
            </ShowcaseCard>
          </div>

          <div className="mt-14 lg:mt-20 brutal-card p-6 lg:p-10 corner-marks bg-[#111] text-white">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest opacity-70">No subscription tease</div>
                <h3 className="font-heading text-2xl md:text-3xl font-black uppercase mt-2">
                  Bring it to your next show.
                </h3>
                <p className="font-mono text-sm opacity-80 mt-2 max-w-xl">
                  Sign in once on the device you carry. Bring a teammate as Staff with one invite.
                  Your inventory follows you — table to table, weekend to weekend.
                </p>
              </div>
              <button
                onClick={handleGoogleLogin}
                className="brutal-btn whitespace-nowrap"
                data-testid="showcase-cta-btn"
              >
                Start Free →
              </button>
            </div>
          </div>
        </div>
      </section>

      <PricingSection onPick={goSignup} />
      <TestimonialsSection />
      <FAQSection />

      <footer className="border-t-2 border-black bg-white">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-5 flex flex-wrap items-center justify-between gap-3">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#4A4A4A]">
            Vendor Vault · Built for Vendors · © {new Date().getFullYear()}
          </div>
          <div className="flex items-center gap-5 font-mono text-[10px] uppercase tracking-widest text-[#4A4A4A]">
            <a href="#pricing" className="hover:text-black underline-offset-2 hover:underline" data-testid="landing-footer-pricing">Pricing</a>
            <a href="#faq" className="hover:text-black underline-offset-2 hover:underline" data-testid="landing-footer-faq">FAQ</a>
            <Link to="/terms" className="hover:text-black underline-offset-2 hover:underline" data-testid="landing-footer-terms">Terms</Link>
            <Link to="/privacy" className="hover:text-black underline-offset-2 hover:underline" data-testid="landing-footer-privacy">Privacy</Link>
            <a href="mailto:support@vendorvaultonline.com" className="hover:text-black underline-offset-2 hover:underline" data-testid="landing-footer-support">Support</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

function ShowcaseCard({ step, title, copy, children, testid, accent }) {
  return (
    <div className="snap-in" data-testid={testid}>
      <div className="flex items-center gap-3 mb-4">
        <span className={`brutal-badge !py-1 !px-2 ${accent ? "!bg-[#C2410C] !text-white" : ""}`}>{step}</span>
        <h3 className="font-heading text-xl font-black uppercase">{title}</h3>
      </div>
      <div className={`${accent ? "transform md:-translate-y-2" : ""}`}>{children}</div>
      <p className="font-mono text-xs text-[#4A4A4A] leading-relaxed mt-4 px-1">
        {copy}
      </p>
    </div>
  );
}

function Feature({ icon, label }) {
  return (
    <div className="brutal-card-sm brutal-card-hover bg-white flex items-center gap-3 px-3 py-2">
      <div className="w-8 h-8 bg-black text-white flex items-center justify-center">{icon}</div>
      <div className="font-mono text-xs uppercase tracking-widest">{label}</div>
    </div>
  );
}


function formatApiError(detail, fallback = "Something went wrong.") {
  if (detail == null) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  if (typeof detail === "object" && detail.message) return detail.message;
  return String(detail);
}

function AuthCard({ onGoogle, onAuthenticated }) {
  const [mode, setMode] = React.useState("signin"); // signin | signup | forgot
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [name, setName] = React.useState("");
  const [agreed, setAgreed] = React.useState(false);
  const [busy, setBusy] = React.useState(false);

  const requiresAgreement = mode === "signup";
  const agreementMissing = requiresAgreement && !agreed;

  const submit = async (e) => {
    e.preventDefault();
    if (agreementMissing) {
      toast.error("Please agree to the Terms & Privacy Policy to create an account.");
      return;
    }
    setBusy(true);
    try {
      if (mode === "signin") {
        const { data } = await api.post("/auth/login", { email: email.trim(), password });
        onAuthenticated(data);
      } else if (mode === "signup") {
        const { data } = await api.post("/auth/signup", { email: email.trim(), password, name: name.trim() });
        toast.success("Welcome to Vendor Vault!");
        onAuthenticated(data);
      } else if (mode === "forgot") {
        await api.post("/auth/password/request-reset", { email: email.trim(), origin_url: window.location.origin });
        toast.success("If that email exists, we just sent a reset link.");
        setMode("signin");
      }
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail, "Couldn't sign you in."));
    } finally { setBusy(false); }
  };

  const handleGoogle = () => {
    if (agreementMissing) {
      toast.error("Please agree to the Terms & Privacy Policy to create an account.");
      return;
    }
    onGoogle();
  };

  const titleByMode = { signin: "Sign In", signup: "Create Account", forgot: "Reset Password" };
  const ctaByMode   = { signin: "Sign in",   signup: "Create account", forgot: "Email me a reset link" };

  return (
    <div className="w-full max-w-md brutal-card p-8 corner-marks snap-in" style={{ animationDelay: "120ms" }} data-testid="auth-card">
      <div className="font-mono text-xs uppercase tracking-widest text-[#4A4A4A]">Check-in Desk</div>
      <h2 className="font-heading text-3xl font-black mt-2 uppercase">{titleByMode[mode]}</h2>

      {mode !== "forgot" && (
        <>
          <button
            onClick={handleGoogle}
            data-testid="google-signin-btn"
            className="brutal-btn brutal-btn-secondary w-full mt-6 !py-3"
          >
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
              <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303C33.972 32.91 29.47 36 24 36c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.651-.389-3.917z"/>
              <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
              <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.448 0-10.033-3.68-11.283-8.691l-6.54 5.04C9.57 39.556 16.227 44 24 44z"/>
              <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8v8c8.945 0 20-6.9 20-20 0-1.341-.138-2.651-.389-3.917z"/>
            </svg>
            Continue with Google
          </button>

          <div className="my-6 flex items-center gap-3">
            <div className="flex-1 h-px bg-black/40"/>
            <span className="font-mono text-[10px] uppercase tracking-widest text-[#4A4A4A]">or with email</span>
            <div className="flex-1 h-px bg-black/40"/>
          </div>
        </>
      )}

      <form onSubmit={submit} className="space-y-3">
        {mode === "signup" && (
          <div>
            <label className="brutal-label">Name</label>
            <input className="brutal-input" placeholder="Your name" value={name}
              onChange={(e)=>setName(e.target.value)} data-testid="auth-name-input" required />
          </div>
        )}
        <div>
          <label className="brutal-label">Email</label>
          <div className="relative">
            <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4A4A4A]" />
            <input
              className="brutal-input !pl-9"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="auth-email-input"
              required
            />
          </div>
        </div>
        {mode !== "forgot" && (
          <div>
            <label className="brutal-label">Password</label>
            <div className="relative">
              <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4A4A4A]" />
              <input
                className="brutal-input !pl-9"
                type="password"
                placeholder={mode === "signup" ? "At least 8 characters" : "••••••••"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={mode === "signup" ? 8 : undefined}
                data-testid="auth-password-input"
                required
              />
            </div>
          </div>
        )}
        {mode === "signup" && (
          <label
            htmlFor="auth-tos-checkbox"
            className="flex items-start gap-2 mt-1 cursor-pointer select-none"
            data-testid="auth-tos-label"
          >
            <input
              id="auth-tos-checkbox"
              type="checkbox"
              className="mt-[3px] w-4 h-4 border-2 border-black accent-[#C2410C] cursor-pointer flex-shrink-0"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              data-testid="auth-tos-checkbox"
              required
            />
            <span className="font-mono text-[11px] text-[#4A4A4A] leading-snug">
              I agree to the{" "}
              <Link to="/terms" target="_blank" rel="noopener" className="text-[#C2410C] underline" data-testid="auth-tos-terms-link">Terms of Service</Link>
              {" "}and{" "}
              <Link to="/privacy" target="_blank" rel="noopener" className="text-[#C2410C] underline" data-testid="auth-tos-privacy-link">Privacy Policy</Link>.
            </span>
          </label>
        )}
        <button type="submit" disabled={busy || agreementMissing} className="brutal-btn w-full !py-3 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="auth-submit-btn">
          {busy ? "…" : ctaByMode[mode]}
        </button>
      </form>

      <div className="mt-5 flex flex-col gap-2 items-start font-mono text-xs">
        {mode === "signin" && (
          <>
            <button onClick={() => setMode("signup")} className="underline text-[#C2410C]" data-testid="auth-switch-signup">
              Don't have an account? <strong>Sign up</strong>
            </button>
            <button onClick={() => setMode("forgot")} className="text-[#4A4A4A] hover:text-black underline" data-testid="auth-switch-forgot">
              Forgot your password?
            </button>
          </>
        )}
        {mode === "signup" && (
          <button onClick={() => setMode("signin")} className="underline text-[#C2410C]" data-testid="auth-switch-signin">
            Already have an account? <strong>Sign in</strong>
          </button>
        )}
        {mode === "forgot" && (
          <button onClick={() => setMode("signin")} className="underline text-[#4A4A4A] hover:text-black" data-testid="auth-switch-back">
            ← Back to sign in
          </button>
        )}
      </div>

      <div className="mt-6 flex items-center justify-between text-[11px] font-mono text-[#4A4A4A] uppercase tracking-widest">
        <span>Secure · Cookie · 7-day</span>
        <span className="brutal-badge !py-0 !px-2 !text-[9px]">Beta</span>
      </div>
    </div>
  );
}
